import { ReactNode } from 'react'
import { useIsSentinelUser } from '../../../authUserProvider'
import s from './styles.module.scss'

interface Props {
    // 'introducer' = sections the introducer can see; 'admin' = Sentinel-only.
    audience: 'introducer' | 'admin'
    // Human-readable introducer name used in the visibility labels
    // (e.g. "Evans Hughes", "PCP Referral").
    introducer: string
    children: ReactNode
}

// Wraps a group of overview-page sections and, for Sentinel admins, labels who
// can see them so it's obvious which sections are shared with the introducer
// and which are internal-only.
//
// - audience="admin": rendered only for Sentinel users (returns null otherwise,
//   matching the self-gating of the admin sections it wraps). Labelled
//   "Sentinel Admins Only · Not visible to {introducer}".
// - audience="introducer": Sentinel users see a "Visible to {introducer}"
//   label around the shared sections; the introducer themselves sees the
//   sections plainly, with no wrapper or label.
export const SectionVisibilityGroup = ({ audience, introducer, children }: Props) => {
    const isSentinelUser = useIsSentinelUser()

    if (audience === 'admin') {
        if (!isSentinelUser) return null
        return (
            <section className={`${s.group} ${s.adminGroup}`}>
                <div className={s.header}>
                    <span className={`${s.badge} ${s.adminBadge}`}>Sentinel Admins Only</span>
                    <span className={s.subtitle}>Not visible to {introducer}</span>
                </div>
                <div className={s.body}>{children}</div>
            </section>
        )
    }

    // Introducer-visible group. The introducer themselves gets the sections with
    // no visibility chrome; only Sentinel admins see the "Visible to X" label.
    if (!isSentinelUser) return <>{children}</>

    return (
        <section className={`${s.group} ${s.introducerGroup}`}>
            <div className={s.header}>
                <span className={`${s.badge} ${s.introducerBadge}`}>Introducer View</span>
                <span className={s.subtitle}>Visible to {introducer}</span>
            </div>
            <div className={s.body}>{children}</div>
        </section>
    )
}

export default SectionVisibilityGroup
